import { showError, showWarning } from './toast';

// Global error handler for unhandled promise rejections
export const setupGlobalErrorHandlers = () => {
  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    console.error('Unhandled promise rejection:', event.reason);
    
    // Prevent the default browser error handling
    event.preventDefault();
    
    // Show user-friendly error message
    showError('An unexpected error occurred. Please try again.');
    
    // Log error details for debugging
    const errorDetails = {
      type: 'unhandledrejection',
      reason: event.reason?.message || event.reason,
      stack: event.reason?.stack,
      timestamp: new Date().toISOString(),
      url: window.location.href
    };
    
    console.error('Unhandled rejection details:', errorDetails);
    
    // In production, send to error reporting service
    if (process.env.NODE_ENV === 'production') {
      reportError(errorDetails);
    }
  });

  // Handle global JavaScript errors
  window.addEventListener('error', (event) => {
    console.error('Global error:', event.error);
    
    const errorDetails = {
      type: 'javascript',
      message: event.message,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      stack: event.error?.stack,
      timestamp: new Date().toISOString(),
      url: window.location.href
    };
    
    console.error('Global error details:', errorDetails);
    
    // Don't show toast for every JS error to avoid spam
    // Only show for critical errors
    if (event.error && !event.error.message?.includes('ResizeObserver')) {
      showError('A technical error occurred. Our team has been notified.');
    }
    
    if (process.env.NODE_ENV === 'production') {
      reportError(errorDetails);
    }
  });
};

// API error handler
export const handleApiError = (error, customMessage = null) => {
  console.error('API Error:', error);
  
  let userMessage = customMessage || 'An error occurred. Please try again.';
  
  if (error.response) {
    // Server responded with error status
    const status = error.response.status;
    const data = error.response.data;
    
    switch (status) {
      case 400:
        userMessage = data?.message || 'Invalid request. Please check your input.';
        break;
      case 401:
        userMessage = 'Session expired. Please log in again.';
        // Redirect to login or refresh token
        setTimeout(() => {
          window.location.href = '/login';
        }, 2000);
        break;
      case 403:
        userMessage = 'Access denied. You don\'t have permission for this action.';
        break;
      case 404:
        userMessage = 'The requested resource was not found.';
        break;
      case 429:
        userMessage = 'Too many requests. Please wait a moment and try again.';
        break;
      case 500:
        userMessage = 'Server error. Our team has been notified.';
        break;
      case 503:
        userMessage = 'Service temporarily unavailable. Please try again later.';
        break;
      default:
        userMessage = data?.message || `Server error (${status}). Please try again.`;
    }
  } else if (error.request) {
    // Network error
    userMessage = 'Network error. Please check your connection and try again.';
  } else {
    // Other error
    userMessage = error.message || 'An unexpected error occurred.';
  }
  
  showError(userMessage);
  
  // Log detailed error info
  const errorDetails = {
    type: 'api',
    message: error.message,
    status: error.response?.status,
    statusText: error.response?.statusText,
    data: error.response?.data,
    config: {
      method: error.config?.method,
      url: error.config?.url,
      baseURL: error.config?.baseURL
    },
    timestamp: new Date().toISOString()
  };
  
  console.error('API Error Details:', errorDetails);
  
  if (process.env.NODE_ENV === 'production') {
    reportError(errorDetails);
  }
  
  return errorDetails;
};

// Form validation error handler
export const handleValidationErrors = (errors) => {
  if (Array.isArray(errors)) {
    errors.forEach(error => showWarning(error));
  } else if (typeof errors === 'object') {
    Object.values(errors).forEach(error => {
      if (Array.isArray(error)) {
        error.forEach(msg => showWarning(msg));
      } else {
        showWarning(error);
      }
    });
  } else {
    showWarning(errors || 'Validation failed');
  }
};

// Async operation wrapper with error handling
export const withErrorHandling = async (operation, errorMessage = null) => {
  try {
    return await operation();
  } catch (error) {
    handleApiError(error, errorMessage);
    throw error; // Re-throw so caller can handle if needed
  }
};

// Retry mechanism for failed operations
export const retryOperation = async (operation, maxRetries = 3, delay = 1000) => {
  let lastError;
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error;
      
      if (attempt === maxRetries) {
        throw error;
      }
      
      // Exponential backoff
      const waitTime = delay * Math.pow(2, attempt - 1);
      console.warn(`Operation failed (attempt ${attempt}/${maxRetries}). Retrying in ${waitTime}ms...`);
      
      await new Promise(resolve => setTimeout(resolve, waitTime));
    }
  }
  
  throw lastError;
};

// Error reporting function
const reportError = async (errorDetails) => {
  try {
    // You can implement your own error reporting service here
    // Example implementations:
    
    // 1. Send to your own API
    // await fetch('/api/errors', {
    //   method: 'POST',
    //   headers: { 'Content-Type': 'application/json' },
    //   body: JSON.stringify(errorDetails)
    // });
    
    // 2. Send to external service like Sentry
    // Sentry.captureException(new Error(errorDetails.message), {
    //   extra: errorDetails
    // });
    
    // 3. Log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('Would report error in production:', errorDetails);
    }
  } catch (reportingError) {
    console.error('Failed to report error:', reportingError);
  }
};

// Network status monitoring
export const setupNetworkMonitoring = () => {
  const updateOnlineStatus = () => {
    if (navigator.onLine) {
      showWarning('Connection restored', { autoClose: 3000 });
    } else {
      showError('Connection lost. Some features may not work properly.');
    }
  };
  
  window.addEventListener('online', updateOnlineStatus);
  window.addEventListener('offline', updateOnlineStatus);
  
  return () => {
    window.removeEventListener('online', updateOnlineStatus);
    window.removeEventListener('offline', updateOnlineStatus);
  };
};
